/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { TransitionGroup } from 'react-transition-group';
import { hideSnackbarAction } from './snackbarAction';
import { StyledBoxedMessage } from '../forms/elements/boxedMessage/StyledBoxedMessage';
import { BoxedErrorMessageDefault, BoxedSuccessMessage } from '../forms/elements/boxedMessage/boxedMessageStyles';
import * as ReactDOM from 'react-dom';
import { LAYER } from '../layout/zIndex';
import { DROP_SHADOW } from '../styles/styles';
import { IAppState } from '../core/redux/IAppState';
import { Snackbar } from './snackbar';
import { FadeInRight } from '../styles/animations';
import { isNilOrEmpty, assoc } from '../language/module';
import { defaultMergeProps } from '../core/redux/sugar';
import { v4 as uuidv4 } from 'uuid';
import {debounce} from 'lodash';


const snackbarContainer = css`
  z-index: ${LAYER.THREE.SNACKBAR};
  position: fixed;
  bottom: 20px;
  right: 20px;
  transition: height .5s ease;
  
  width: 412px;
           
  /* On smaller screens, the Snackbar should be wider so it's still readable */
  @media(max-width: 432px) {
    width: 94%;
  }
`;

const snackbarMessage = css`
  min-width: 395px;
  margin-top: 20px;
  border-radius: 11px;
  transition: all .5s ease;
  ${DROP_SHADOW};
`;


type ShownSnackbar = {
  key: string;
  snackbar: Snackbar;
  clearTimeout: () => void;
};


type State = {
  maxSnackbarCount: number;
  showing: ShownSnackbar[];
};


/**
 * SnackbarHost: a component that connects to the Redux store and shows Snackbars when Snackbar data enters the store. This
 * component will enforce the snackbar timeout and clear the Redux store state when a Snackbar is done showing.
 *
 * This is a higher order component that wraps a child and shows Snackbars over the child.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  // Map state to props
  (state: IAppState) => ({
    snackbars: state.snackbars,
  }),
  // Map dispatch to props
  (dispatch) => ({clearSnackbar: (snackbar: Snackbar) => dispatch(hideSnackbarAction(snackbar))}),
  defaultMergeProps,
  {
    areStatesEqual: (nextState, prevState) => prevState.snackbars === nextState.snackbars,
  },
)(class SnackbarHost extends React.PureComponent<{
  snackbars?: Snackbar[];
  clearSnackbar?: (snackbar: Snackbar) => void;
  snackbarCss?: SerializedStyles;
  rootCss?: SerializedStyles;
}, State> {
  constructor(props) {
    super(props);

    this.state = {
      showing:          [],
      maxSnackbarCount: this.calcMaxSnackbarCount(window.innerHeight),
    } as State;

    if (!isNilOrEmpty(props.Snackbars)) {
      R.take(Math.min(this.state.maxSnackbarCount, this.props.snackbars!.length), this.props.snackbars!)
        .map(this.showSnackbar);
    }
  }


  private clearAllTimeouts = () => this.state.showing.map(snackbar => snackbar.clearTimeout());


  private hideSnackbar = (snackbar: Snackbar) => this.setState(
    prev => assoc('showing', prev.showing.filter(model => model.snackbar !== snackbar), prev),
  );


  private dismissSnackbarModel = (snackbar: Snackbar): void => {
    this.props.clearSnackbar!(snackbar);
    this.hideSnackbar(snackbar);
  };


  private dismissShownSnackbar = (snackbar: ShownSnackbar): void => {
    snackbar.clearTimeout();
    this.dismissSnackbarModel(snackbar.snackbar);
  };


  /**
   * When the window size changes, we might not be able to show as many snackbars as we're showing right now. We have to
   * reset some of them back into the queue to be shown later. This method does that.
   */
  private resetSnackbar = (snackbar: ShownSnackbar): void => {
    snackbar.clearTimeout();
    this.hideSnackbar(snackbar.snackbar);
  };


  private showSnackbar = (snackbar: Snackbar) => {
    const timeoutHandle = snackbar.timeoutMs < 0
                          ? undefined
                          : window.setTimeout(() => this.dismissSnackbarModel(snackbar), snackbar.timeoutMs);

    const shownSnackbar = {
      key:          uuidv4(),
      snackbar:     snackbar,
      clearTimeout: () => timeoutHandle ? window.clearTimeout(timeoutHandle) : undefined,
    };

    this.setState(prev => assoc('showing', prev.showing.concat(shownSnackbar), prev));
  };


  public calcMaxSnackbarCount = (winHeight: number) => {
    const snackbarHeight = 114; // 3 lines of text
    const height         = winHeight * .66;
    return Math.max(Math.floor(height / snackbarHeight), 1);
  };


  private processIncomingSnackbars = debounce(() => {
    const unusedSlots = this.state.maxSnackbarCount - this.state.showing.length;
    const toShow      = this.props.snackbars!.filter(
      snackbar => !R.any(showingSnackbar => showingSnackbar.snackbar === snackbar, this.state.showing),
    );

    R.take(Math.min(unusedSlots, toShow.length), toShow).map(this.showSnackbar);
  }, 500);


  public onResize = debounce(() => {
    // Calculate # of snackbars we can show at a time
    const snackbarCount = this.calcMaxSnackbarCount(window.innerHeight);

    this.setState(prev => assoc('maxSnackbarCount', snackbarCount, prev));

    // If showing more Snackbars than new number, reset timers on some showing Snackbars and hide
    if (this.state.showing.length > snackbarCount) {
      R.takeLast(this.state.showing.length - snackbarCount, this.state.showing).map(this.resetSnackbar);
    }
  }, 1000);


  private makeOnClose = (snackbar: ShownSnackbar) => () => this.dismissShownSnackbar(snackbar);


  public componentDidUpdate(prevProps, prevState: State) {
    // Nothing Changed
    if (
      this.state.maxSnackbarCount === prevState.maxSnackbarCount
      && this.state.showing === prevState.showing
      && this.props.snackbars === prevProps.snackbars
    ) {
      return;
    }

    // Re-calculate snackbars
    this.processIncomingSnackbars();
  }


  public componentDidMount(): void {
    window.addEventListener('resize', this.onResize);
  }


  public componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);

    this.clearAllTimeouts();
  }

  private nodeRef = React.createRef<HTMLDivElement>();


  public render = () => ReactDOM.createPortal(
    /* @ts-ignore*/
    <TransitionGroup css={snackbarContainer} nodeRef={this.nodeRef}>
      {
        this.state.showing!.map(
          snackbar => (
            <FadeInRight key={snackbar.key} in={true}>
              <StyledBoxedMessage onClose={this.makeOnClose(snackbar)}
                                  boxStyle={snackbar.snackbar.isError ? BoxedErrorMessageDefault : BoxedSuccessMessage}
                                  message={snackbar.snackbar.message!}
                                  extraCss={css`
                                    ${snackbarMessage};
                                    ${this.props.snackbarCss};
                                  `}/>
            </FadeInRight>
          ),
        )
      }
    </TransitionGroup>,
    document.body,
  );
});
